<template>
  <v-card class="elevation-0">
    <v-card-text class="pa-0 pa-7">
      <!-- Header with Add Button -->
      <div class="d-flex align-center justify-space-between mb-4">
        <div>
          <h3 class="text-h6 mb-1">
            <v-icon left small>mdi-currency-usd</v-icon>
            {{ $t('price_list.name') || 'Price Lists' }}
          </h3>
          <p class="text-caption mb-0">
            {{ $t('price_list.description') || 'Manage different pricing structures for your products and services' }}
          </p>
        </div>
        <v-btn
          color="primary"
          small
          @click="addPriceList(0)"
          v-can="'setting.price-list'"
        >
          <v-icon left small>mdi-plus</v-icon>
          {{ $t('price_list.button.add_new') || 'Add Price List' }}
        </v-btn>
      </div>

      <!-- Price Lists Table -->
      <v-data-table
        :headers="headers"
        :items="price_lists"
        :loading="loading"
        :items-per-page="10"
        class="elevation-1"
        :loading-text="$t('loading') || 'Loading...'"
        :no-data-text="$t('no_data') || 'No price lists found'"
      >
        <!-- System Badge -->
        <template v-slot:[`item.system`]="{ item }">
          <v-chip
            v-if="item.system"
            x-small
            color="orange"
            text-color="white"
          >
            <v-icon left x-small>mdi-lock</v-icon>
            {{ $t('price_list.badge.system') || 'System' }}
          </v-chip>
          <v-chip
            v-else
            x-small
            color="green"
            text-color="white"
          >
            <v-icon left x-small>mdi-account</v-icon>
            {{ $t('price_list.badge.custom') || 'Custom' }}
          </v-chip>
        </template>

        <!-- Actions -->
        <template v-slot:[`item.actions`]="{ item, index }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                color="success"
                @click="addPriceList(index + 1)"
                v-bind="attrs"
                v-on="on"
                class="mr-1"
              >
                <v-icon small>mdi-plus-circle</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('price_list.button.add_after') || 'Add after this' }}</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                color="error"
                @click="removePriceList(item.id, index)"
                :disabled="!!item.system"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </template>
            <span>
              {{ item.system
                ? $t('price_list.button.cannot_remove_system') || 'Cannot remove system price list'
                : $t('price_list.button.remove') || 'Remove'
              }}
            </span>
          </v-tooltip>
        </template>

        <!-- Custom no data slot -->
        <template v-slot:no-data>
          <v-alert
            type="info"
            text
            class="ma-4"
          >
            <v-icon left>mdi-information</v-icon>
            {{ $t('price_list.no_data') || 'No price lists configured. Click "Add Price List" to create one.' }}
          </v-alert>
        </template>
      </v-data-table>

      <!-- Help Section -->
      <v-alert
        type="info"
        text
        dense
        border="left"
        class="mt-4"
      >
        <div class="text-subtitle-2 font-weight-medium mb-2">
          {{ $t('price_list.help.title') || 'About Price Lists' }}
        </div>
        <div class="text-caption">
          {{ $t('price_list.help.description') || 'Price lists allow you to set different prices for the same products. You can create multiple price lists for different customer groups, locations, or special promotions.' }}
        </div>
        <ul class="text-caption mt-2 pl-4">
          <li>{{ $t('price_list.help.tip1') || 'System price lists cannot be removed' }}</li>
          <li>{{ $t('price_list.help.tip2') || 'Click the green plus button to insert a new price list at a specific position' }}</li>
          <li>{{ $t('price_list.help.tip3') || 'Price list order affects priority when multiple lists apply' }}</li>
        </ul>
      </v-alert>
    </v-card-text>

    <!-- Add Price List Dialog -->
    <add-price-list
      :position="add_position"
      :display="show_add_dialog"
      @close="hideAddDialog"
      @created="priceListCreated"
    />

    <!-- Confirmation Dialog -->
    <v-dialog
      v-model="deleteDialog"
      max-width="400"
    >
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="warning">mdi-alert</v-icon>
          {{ $t('price_list.confirm.title') || 'Confirm Deletion' }}
        </v-card-title>
        <v-card-text>
          {{ $t('price_list.confirm.remove') || 'Are you sure you want to remove this price list? This action cannot be undone.' }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="deleteDialog = false"
          >
            {{ $t('button.cancel') || 'Cancel' }}
          </v-btn>
          <v-btn
            color="error"
            @click="confirmDelete"
          >
            <v-icon left>mdi-delete</v-icon>
            {{ $t('button.delete') || 'Delete' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success Snackbar -->
    <v-snackbar
      v-model="snackbar"
      :timeout="3000"
      :color="snackbarColor"
      bottom
      right
    >
      <v-icon left>{{ snackbarIcon }}</v-icon>
      {{ snackbarText }}
    </v-snackbar>
  </v-card>
</template>

<script>
import AddPriceList from '../Dialogs/AddPriceList'

export default {
  name: 'PriceLists',
  components: { AddPriceList },
  data () {
    return {
      price_lists: [],
      loading: false,
      add_position: null,
      show_add_dialog: false,
      deleteDialog: false,
      deleteTarget: null,
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      snackbarIcon: 'mdi-check-circle',
      headers: [
        {
          text: this.$t('price_list.label.name') || 'Name',
          value: 'name',
          sortable: true
        },
        {
          text: this.$t('price_list.label.description') || 'Description',
          value: 'description',
          sortable: false
        },
        {
          text: this.$t('price_list.label.type') || 'Type',
          value: 'system',
          sortable: true,
          width: '120px'
        },
        {
          text: this.$t('price_list.label.actions') || 'Actions',
          value: 'actions',
          sortable: false,
          align: 'right',
          width: '120px'
        }
      ]
    }
  },
  methods: {
    addPriceList (position) {
      this.add_position = position
      this.showAddDialog()
    },
    showAddDialog () {
      this.show_add_dialog = true
    },
    hideAddDialog () {
      this.show_add_dialog = false
    },
    priceListCreated (data) {
      this.price_lists.splice(this.add_position, 0, data)
      this.showSnackbar(this.$t('price_list.message.created') || 'Price list created successfully')
    },
    removePriceList (id, index) {
      this.deleteTarget = { id, index }
      this.deleteDialog = true
    },
    async confirmDelete () {
      if (!this.deleteTarget) return

      this.loading = true
      try {
        const data = await this.$configService.removePriceList(this.deleteTarget.id)

        if (data.status === 'success' || data.status === 200) {
          this.price_lists.splice(this.deleteTarget.index, 1)
          this.showSnackbar(this.$t('price_list.message.removed') || 'Price list removed successfully')
        } else {
          this.showSnackbar(data.message || 'Failed to remove price list', 'error')
        }
      } catch (error) {
        this.globalErrHandle(error)
        this.showSnackbar('An error occurred while removing the price list', 'error')
      } finally {
        this.loading = false
        this.deleteDialog = false
        this.deleteTarget = null
      }
    },
    showSnackbar (text, type = 'success') {
      this.snackbarText = text
      this.snackbarColor = type
      this.snackbarIcon = type === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle'
      this.snackbar = true
    },
    async loadPriceLists () {
      this.loading = true
      try {
        const data = await this.$configService.getPriceLists()
        this.price_lists = data
      } catch (error) {
        this.globalErrHandle(error)
        this.showSnackbar('Failed to load price lists', 'error')
      } finally {
        this.loading = false
      }
    }
  },
  mounted () {
    // Emit title and description to parent
    this.$emit('parent-title', this.$t('price_list.name') || 'Price Lists')
    this.$emit('parent-description', this.$t('price_list.page_description') || 'Configure and manage pricing structures for different customer groups and scenarios')

    // Load price lists
    this.loadPriceLists()
  }
}
</script>

<style scoped>
/* Remove default card shadow for seamless integration */
.v-card {
  background: transparent;
}

/* Ensure table fits well */
::v-deep .v-data-table {
  background: white;
}

.theme--dark ::v-deep .v-data-table {
  background: #1e1e1e;
}
</style>
